import { alpha } from '@mui/system';

import { BLUE, GREEN, ORANGE, RED, YELLOW } from 'theme/colors';
import globalMessages from 'translations/messages/global-messages';
import { parseToRgba } from 'utils/color.utils';
import { HEADING_TAG, TEXT_ALIGN } from 'utils/editor.utils';

export const MEETING_GENERATION_STATUS = {
  GENERATING: 'Generating',
  SUCCESS: 'Success',
  FAILURE: 'Failure',
};

export const NOTE_TYPE = {
  observation: 'Observation',
  form: 'Form',
  notes: 'Notes',
};

export const NOTE_PROPERTY = {
  order: 'order',
  note: 'note',
  dueDate: 'due_date',
  statusId: 'status_id',
  assigneesTo: 'assignees_to',
  companies: 'assigned_companies_ids',
  mediaResources: 'media_resources',
  planId: 'plan_id',
  positionX: 'position_x',
  positionY: 'position_y',
};

export const REMARK_SOURCE = {
  MEETING: 'meeting',
  REMARK_DETAILS: 'remark_details',
  LISTS: 'remark_list',
};

export const DEFAULT_HEADING_TAG = HEADING_TAG.PARAGRAPH;

export const DEFAULT_ALIGN = TEXT_ALIGN.LEFT;

export const COLORS = {
  BLUE: parseToRgba(BLUE),
  GREEN: parseToRgba(GREEN),
  YELLOW: parseToRgba(YELLOW),
  ORANGE: parseToRgba(ORANGE),
  RED: parseToRgba(RED),
};

export const DELAYED_SAVE_TIMEOUT = 500; // milliseconds

// toutes les tailles ci-dessous sont en pixels
export const SECTION_SUMMARY_MARGIN_RIGHT = 40;

export const SECTION_SUMMARY_CHEVRON_WIDTH = 32;

export const SECTION_SUMMARY_TOP_GAP = 8;

export const SECTION_DRAG_HANDLE_SIZE = 18;

export const SECTION_SUMMARY_BOTTOM_MARGIN_LEFT =
  SECTION_SUMMARY_CHEVRON_WIDTH + SECTION_SUMMARY_TOP_GAP;

export const SECTION_TREE_ITEM_MARGIN = 38;

export const getTabs = intl => [
  {
    id: 0,
    nameTranslation: intl.formatMessage(globalMessages.properties),
  },
  {
    id: 1,
    nameTranslation: intl.formatMessage(globalMessages.meeting_minutes),
  },
  {
    id: 2,
    nameTranslation: intl.formatMessage(globalMessages.meeting_attendees),
  },
];

export const getPickerTextColors = theme => ({
  DEFAULT: {
    rgbaValue: parseToRgba(theme.palette.text.dark),
    strValue: 'blue_dark',
    border: parseToRgba(theme.palette.text.dark),
    borderSelected: theme.palette.text.primary,
  },
  BLUE: {
    rgbaValue: COLORS.BLUE,
    strValue: 'blue',
    border: COLORS.BLUE,
    borderSelected: '#1565c0',
  },
  GREEN: {
    rgbaValue: COLORS.GREEN,
    strValue: 'green',
    border: COLORS.GREEN,
    borderSelected: '#2e7d32',
  },
  YELLOW: {
    rgbaValue: COLORS.YELLOW,
    strValue: 'yellow',
    border: COLORS.YELLOW,
    borderSelected: '#f9a825',
  },
  ORANGE: {
    rgbaValue: COLORS.ORANGE,
    strValue: 'orange',
    border: COLORS.ORANGE,
    borderSelected: '#ef6c00',
  },
  RED: {
    rgbaValue: COLORS.RED,
    strValue: 'red',
    border: COLORS.RED,
    borderSelected: '#b71c1c',
  },
});

export const getPickerBackgroundColors = theme => {
  const opacity = 0.3;
  return {
    DEFAULT: {
      rgbaValue: parseToRgba('transparent'),
      strValue: 'transparent',
      border: theme.palette.divider,
      borderSelected: theme.palette.text.primary,
    },
    BLUE_LIGHT: {
      rgbaValue: parseToRgba(alpha(COLORS.BLUE, opacity)),
      strValue: 'blue_light',
      border: COLORS.BLUE,
      borderSelected: '#1565c0',
    },
    GREEN_LIGHT: {
      rgbaValue: parseToRgba(alpha(COLORS.GREEN, opacity)),
      strValue: 'green_light',
      border: COLORS.GREEN,
      borderSelected: '#2e7d32',
    },
    YELLOW_LIGHT: {
      rgbaValue: parseToRgba(alpha(COLORS.YELLOW, opacity)),
      strValue: 'yellow_light',
      border: COLORS.YELLOW,
      borderSelected: '#f9a825',
    },
    ORANGE_LIGHT: {
      rgbaValue: parseToRgba(alpha(COLORS.ORANGE, opacity)),
      strValue: 'orange_light',
      border: COLORS.ORANGE,
      borderSelected: '#ef6c00',
    },
    RED_LIGHT: {
      rgbaValue: parseToRgba(alpha(COLORS.RED, opacity)),
      strValue: 'red_light',
      border: COLORS.RED,
      borderSelected: '#b71c1c',
    },
  };
};

export const getDefaultTextColor = theme => getPickerTextColors(theme).DEFAULT;

export const getDefaultBackgroundColor = theme => getPickerBackgroundColors(theme).DEFAULT;

export const getDate = (intl, date) =>
  intl.formatDate(date, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export const getHour = (intl, date) =>
  intl.formatDate(date, {
    hour: 'numeric',
    minute: 'numeric',
  });

// complète une section avec des enfants factices
export const generateEmptySubsections = section => ({
  ...section,
  children: section.children_sections_count
    ? Array.from({ length: section.children_sections_count }, (_, subsectionIndex) => ({
        id: `section-${section.id}-subsection-${subsectionIndex}`,
        name: '',
        notLoaded: true,
      }))
    : undefined,
});

// complète un tableau de sections avec des enfants factices
export const populateSectionsWithFakeChildren = sections =>
  sections.map(section => ({ ...generateEmptySubsections(section) }));

// met à jour les sous-sections d'une section spécifique dans un arbre
export const updateNodeSubsections = (sections, sectionId, newSubsections) =>
  sections.map(section => {
    // si la section correspond à l'id ciblé, on remplace ses enfants
    if (section.id === sectionId) return { ...section, children: newSubsections };

    // si la section a des enfants, on applique la fonction récursivement
    if (section.children) {
      return {
        ...section,
        children: updateNodeSubsections(section.children, sectionId, newSubsections),
      };
    }
    // sinon, on retourne la section
    return section;
  });

// met à jour les préfixes de toutes les sections et sous-sections dans un arbre
export const updateTreePrefixes = (sections, prefix = '') =>
  sections.map((section, index) => {
    const newPrefix = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
    return {
      ...section,
      prefix: newPrefix,
      children: section.children
        ? updateTreePrefixes(section.children, newPrefix)
        : section.children,
    };
  });

// met à jour les préfixes des sous-sections d'une liste de sections
export const updateSubsectionsPrefixes = (subsections, parentPrefix) =>
  subsections.map((section, index) => {
    const newPrefix = `${parentPrefix}.${index + 1}`;
    return {
      ...section,
      prefix: newPrefix,
      children: section.children
        ? updateSubsectionsPrefixes(section.children, newPrefix)
        : section.children,
    };
  });

// met à jour les préfixes de tout l'arbre et l'ordre d'une section spécifique
export const updateTreeAfterReorder = (
  sections,
  sectionId,
  parentSectionId,
  newOrder,
  prefix = '',
) =>
  sections.map((section, index) => {
    const newPrefix = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
    const updatedSection = { ...section, prefix: newPrefix };

    // met à jour l'ordre si c'est la section ciblée
    if (section.id === sectionId) {
      updatedSection.order = newOrder;
      updatedSection.parent_section_id = parentSectionId;
    }

    // met à jour récursivement les enfants
    if (section.children) {
      updatedSection.children_sections_count = section.children.length;
      updatedSection.children = updateTreeAfterReorder(
        section.children,
        sectionId,
        parentSectionId,
        newOrder,
        newPrefix,
      );
    }

    return updatedSection;
  });

export const hasTextNoteProperties = note =>
  note.due_date ||
  note.status_id ||
  (note.assignees_to && note.assignees_to.length > 0) ||
  (note.assigned_companies_ids && note.assigned_companies_ids.length > 0) ||
  (note.media_resources && note.media_resources.length > 0);
